/*!
 * Copyright © 2022-2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { EpochTimeUtilities } from '../../../../utils';
import {
  EBoxType,
  EPermissionStatus,
  EPingState,
  EPlotType,
  EVehicleStatusType,
  IStatusPanelItem,
  IUserPermissions,
  IUserSettings,
  PingAndResetSupportedDevices
} from '../../../core/models';
import { LiveChatScriptLoaderService } from '../../../external/services';
import { EHealthCheckRequestState, IDeviceHealthCheckHistory, IDeviceHealthCheckLiveChatParams } from '../../models';

@Component({
  selector: 'fm-device-health-check',
  templateUrl: './device-health-check.component.html',
  styleUrls: ['./device-health-check.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceHealthCheckComponent implements OnInit, OnChanges {
  @Input()
  statusItem: IStatusPanelItem;
  @Input()
  esn: string;
  @Input()
  boxType: EBoxType;
  @Input()
  userSettings: IUserSettings;
  @Input()
  accountId: number;
  @Input()
  pingState: EHealthCheckRequestState;
  @Input()
  resetState: EHealthCheckRequestState;
  @Input()
  history: IDeviceHealthCheckHistory[];
  @Input()
  historyLoading: boolean;
  @Input()
  userPermissions: IUserPermissions;

  @Output()
  sendPingClicked: EventEmitter<string> = new EventEmitter<string>();

  lastUpdateDate: Date;
  isVehicleOrPoweredAsset: boolean;
  isNonPoweredAsset: boolean;
  isAsset: boolean;
  liveChatParams: IDeviceHealthCheckLiveChatParams;
  showContactSupportSection: boolean;
  EVehicleStatusType = EVehicleStatusType;
  EPlotType = EPlotType;
  isPingSupported: boolean;
  isUpdating$ = new BehaviorSubject(false);

  constructor(
    private readonly _liveChatScriptLoaderService: LiveChatScriptLoaderService,
    private readonly _changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._liveChatScriptLoaderService.loadScript();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isPingSupported = PingAndResetSupportedDevices.includes(this.boxType);
    this.lastUpdateDate = EpochTimeUtilities.fromDotNetTicksToDate(this.statusItem?.ticks);
    this.isVehicleOrPoweredAsset = this.statusItem?.type === EPlotType.vehicle || this.statusItem?.type === EPlotType.poweredAsset;
    this.isNonPoweredAsset = this.statusItem?.type === EPlotType.asset;
    this.isAsset = this.statusItem?.type === EPlotType.asset || this.statusItem?.type === EPlotType.poweredAsset;
    this.liveChatParams = {
      accountId: this.accountId?.toString(),
      ESN: this.esn,
      lastUpdate: this.statusItem?.time,
      status: this._getliveChatParams(),
      trackerType: this.boxType,
      vehicleName: this.statusItem?.vehicleName
    };

    if (changes['statusItem'] && this._hasStatusItemUpdated(changes['statusItem']?.currentValue, changes['statusItem']?.previousValue)) {
      this.isUpdating$.next(true);
      setTimeout(() => {
        this.isUpdating$.next(false);
        this._changeDetector.markForCheck();
      }, 1000);
    }
  }

  onPingStateEvent(pingState: EPingState): void {
    if (pingState === EPingState.Sending) {
      this.sendPingClicked.emit(this.esn);
    }
    this.showContactSupportSection =
      pingState === EPingState.Sent &&
      this._isLanguageSupportedForContactSupport(this.userSettings.Language) &&
      this._hasLiveChatPermissions() &&
      this._isLiveChatEnabled();
  }

  resetClicked(): void {
    this.sendPingClicked.emit(this.esn);
  }

  onShowContactUs(showContactUs: boolean): void {
    this.showContactSupportSection =
      showContactUs &&
      this._isLanguageSupportedForContactSupport(this.userSettings.Language) &&
      this._hasLiveChatPermissions() &&
      this._isLiveChatEnabled();
    this._changeDetector.detectChanges();
  }

  private _isLiveChatEnabled(): boolean {
    return (
      environment.config.deviceHealthCheckLiveChat.enabled ||
      environment.config.deviceHealthCheckAmazonConnectChat.enabled ||
      environment.config.deviceHealthCheckAgentCoreChat.enabled
    );
  }

  private _hasLiveChatPermissions(): boolean {
    return this.userPermissions?.liveChat === EPermissionStatus.ReadOnly || this.userPermissions?.liveChat === EPermissionStatus.Full;
  }

  private _isLanguageSupportedForContactSupport(language: string): boolean {
    const supportedLanguages = ['en-us', 'en-ca'];
    return supportedLanguages.includes(language.trim().toLowerCase());
  }

  private _hasStatusItemUpdated(first: IStatusPanelItem, second: IStatusPanelItem): boolean {
    return first?.id === second?.id && first?.timeUnits !== second?.timeUnits;
  }

  private _getliveChatParams(): string {
    if (this.resetState === EHealthCheckRequestState.Failure) {
      return 'Reset Failed';
    }
    if (this.resetState === EHealthCheckRequestState.Success) {
      return 'Reset Success';
    }
    if (this.pingState === EHealthCheckRequestState.Failure) {
      return 'Ping Failed';
    }
    if (this.pingState === EHealthCheckRequestState.Success) {
      return 'Ping Success';
    }
    return 'Ping Sent';
  }
}
